import React, { useState, useMemo, useRef, useLayoutEffect } from 'react'
import MapTypeGenerator from './MapTypeGenerator'
import { MotifGeneratorType } from './types'
import KaleidoscopeGeneratorCanvas from './KaleidoscopeGeneratorCanvas'
import { useContextMotifGenerator } from '../../contexts/motifGeneratorContext'
import PenroseGenerator from './PenroseGenerator'
import SquareLayoutGeneratorCanvas from './SquareLayoutGeneratorCanvas'
import CircleLayoutGeneratorCanvas from './CircleLayoutGeneratorCanvas'
import FreeLayoutGenerator from './FreeLayoutGenerator'
import ExportLayoutModal from './ExportLayoutModal'
import { ExportLayoutModalResponse } from './ExportLayoutModal'

interface GeneratorProps {
  exportResponse?: ExportLayoutModalResponse
  onExportComplete?: () => void
}

type GeneratorComponent = React.ComponentType<GeneratorProps>

// Default export type for generators without modal SINGLE or TILES
const DEFAULT_EXPORT_TYPE = ExportLayoutModalResponse.SINGLE

// Configuration to determine which generators should show the export modal
const EXPORT_MODAL_CONFIG: Record<MotifGeneratorType, boolean> = {
  [MotifGeneratorType.MAP]: true,
  [MotifGeneratorType.SQUARE]: true,
  [MotifGeneratorType.CIRCLE]: true,
  [MotifGeneratorType.PENROSE]: true,
  [MotifGeneratorType.KALEIDO_SQUARE]: true,
  [MotifGeneratorType.KALEIDO_PENTA]: true,
  [MotifGeneratorType.KALEIDO_HEXA]: true,
  [MotifGeneratorType.KALEIDO_OCTA]: true,
  [MotifGeneratorType.FREE]: true,
}

const GENERATOR_COMPONENTS: Record<MotifGeneratorType, GeneratorComponent> = {
  [MotifGeneratorType.MAP]: MapTypeGenerator,
  [MotifGeneratorType.SQUARE]: SquareLayoutGeneratorCanvas,
  [MotifGeneratorType.CIRCLE]: CircleLayoutGeneratorCanvas,
  [MotifGeneratorType.PENROSE]: PenroseGenerator,
  [MotifGeneratorType.KALEIDO_SQUARE]: (props: GeneratorProps) => (
    <KaleidoscopeGeneratorCanvas type={MotifGeneratorType.KALEIDO_SQUARE} {...props} />
  ),
  [MotifGeneratorType.KALEIDO_PENTA]: (props: GeneratorProps) => (
    <KaleidoscopeGeneratorCanvas type={MotifGeneratorType.KALEIDO_PENTA} {...props} />
  ),
  [MotifGeneratorType.KALEIDO_HEXA]: (props: GeneratorProps) => (
    <KaleidoscopeGeneratorCanvas type={MotifGeneratorType.KALEIDO_HEXA} {...props} />
  ),
  [MotifGeneratorType.KALEIDO_OCTA]: (props: GeneratorProps) => (
    <KaleidoscopeGeneratorCanvas type={MotifGeneratorType.KALEIDO_OCTA} {...props} />
  ),
  [MotifGeneratorType.FREE]: FreeLayoutGenerator,
}

const MotifPatternGenerator = () => {
  const {
    selectedGeneratorType,
    setExportInvoked,
    exportModal,
    setExportModal,
    setStartExport,
    setLoading,
  } = useContextMotifGenerator()
  const [exportResponse, setExportResponse] = useState<ExportLayoutModalResponse | null>(null)
  const lastExportGeneratorTypeRef = useRef<MotifGeneratorType | null>(null)

  const handleExportModalResponse = (response: ExportLayoutModalResponse) => {
    setExportResponse(response)
    setExportModal(false)
    lastExportGeneratorTypeRef.current = selectedGeneratorType
  }

  // Reset exportResponse and exportModal when generator type changes to prevent unwanted exports
  useLayoutEffect(() => {
    setExportResponse(null)
    setExportModal(false)
    setExportInvoked(false)
    setStartExport(false)
    setLoading(false)
    // Reset context export states to prevent cross-generator contamination
    lastExportGeneratorTypeRef.current = null
  }, [selectedGeneratorType, setExportModal, setExportInvoked, setStartExport, setLoading])

  // Handle auto-export for generators without modal (MAP, PENROSE) - only when export is explicitly invoked
  React.useEffect(() => {
    if (exportModal && !EXPORT_MODAL_CONFIG[selectedGeneratorType]) {
      setExportResponse(DEFAULT_EXPORT_TYPE)
      setExportModal(false)
      lastExportGeneratorTypeRef.current = selectedGeneratorType
    }
  }, [exportModal, selectedGeneratorType, setExportModal])

  const handleExportComplete = () => {
    setExportResponse(null)
    setStartExport(false)
    setExportInvoked(false)
    // Reset refs when export completes to prevent cross-generator contamination
    lastExportGeneratorTypeRef.current = null
  }

  const selectedGenerator = useMemo(() => {
    // Only pass exportResponse if the current generator type is the same as where the export was initiated
    const shouldPassExportResponse =
      exportResponse && lastExportGeneratorTypeRef.current === selectedGeneratorType

    const effectiveExportResponse = shouldPassExportResponse ? exportResponse : undefined

    if (selectedGeneratorType === MotifGeneratorType.FREE) {
      return (
        <FreeLayoutGenerator
          exportResponse={effectiveExportResponse}
          onExportComplete={handleExportComplete}
        />
      )
    }

    const GeneratorComponent =
      GENERATOR_COMPONENTS[selectedGeneratorType] || GENERATOR_COMPONENTS[MotifGeneratorType.MAP]

    return (
      <GeneratorComponent
        exportResponse={effectiveExportResponse}
        onExportComplete={handleExportComplete}
      />
    )
  }, [selectedGeneratorType, exportResponse])

  return (
    <>
      {selectedGenerator}
      {exportModal && EXPORT_MODAL_CONFIG[selectedGeneratorType] && (
        <ExportLayoutModal
          responseHandlerFn={handleExportModalResponse}
          setExportInvoked={setExportInvoked}
          setExportModal={setExportModal}
        />
      )}
    </>
  )
}

export default MotifPatternGenerator
